import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface SelectedItem {
  id: string;
  isLinkedCopy: boolean;
  sortOrder?: number;
}

export interface SelectionState {
  selectedProjects: SelectedItem[];
  selectedLessons: SelectedItem[];
  selectedSlides: SelectedItem[];
}

interface UnlinkModalProps {
  type: "Slide" | "Project" | "Lesson";
  itemId: string;
  originalItemId?: string | null;
}

interface WarningCallbacks {
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Defaults to "Warning" — override for a neutral confirmation that isn't actually a warning. */
  title?: string;
}

export interface ModalState {
  isMoveModalOpen: boolean;
  isMoveSlideModalOpen: boolean;
  isTrashModalOpen: boolean;
  isHideModalOpen: "hide" | "show" | false;
  isDeleteModalOpen: boolean;
  isRestoreModalOpen: "Slides" | "Projects" | "Lessons" | "Items" | false;
  isSelectionMode: boolean;
  isDuplicateSlideModalOpen: string | false;
  isDuplicateLessonModalOpen: string | false;
  isDuplicateProjectModalOpen: string | false;
  isSetTagsModalOpen: boolean;
  isSetDomainModalOpen: boolean;
  isUnlinkModalOpen: UnlinkModalProps | false;
  isWarningModalOpen: string | false;
  isLinkedParentWarningModalOpen: boolean;
  warningCallbacks: WarningCallbacks | null;
  warningError: unknown;
}

export interface SelectionContextType {
  // Selection state
  selectedProjects: SelectedItem[];
  selectedLessons: SelectedItem[];
  selectedSlides: SelectedItem[];

  // Derived state for backward compatibility
  selectedProjectIds: string[];
  selectedLessonIds: string[];
  selectedSlideIds: string[];

  // Selection actions
  toggleProject: (id: string, isLinkedCopy: boolean) => void;
  toggleLesson: (id: string, isLinkedCopy: boolean, sortOrder?: number) => void;
  toggleSlide: (id: string, isLinkedCopy: boolean, sortOrder?: number) => void;
  selectProject: (id: string, isLinkedCopy: boolean) => void;
  selectProjects: (items: SelectedItem[]) => void;
  selectLesson: (id: string, isLinkedCopy: boolean, sortOrder?: number) => void;
  selectLessons: (items: SelectedItem[]) => void;
  selectSlide: (id: string, isLinkedCopy: boolean, sortOrder?: number) => void;
  selectSlides: (items: SelectedItem[]) => void;
  clearAllSelections: () => void;

  // Modal state
  isMoveModalOpen: boolean;
  isMoveSlideModalOpen: boolean;
  isTrashModalOpen: boolean;
  isHideModalOpen: "hide" | "show" | false;
  isDeleteModalOpen: boolean;
  isDuplicateSlideModalOpen: string | false;
  isDuplicateLessonModalOpen: string | false;
  isDuplicateProjectModalOpen: string | false;
  isSetTagsModalOpen: boolean;
  isSetDomainModalOpen: boolean;
  isRestoreModalOpen: "Slides" | "Projects" | "Lessons" | "Items" | false;
  isUnlinkModalOpen: UnlinkModalProps | false;
  isWarningModalOpen: string | false;
  isLinkedParentWarningModalOpen: boolean;
  warningCallbacks: WarningCallbacks | null;
  warningError: unknown;

  // Merge mode — a dedicated "absorb slides into a master's subQuizzes" flow, separate from the
  // general selection mode so it can hard-disable non-eligible slides without touching bulk actions.
  // `mergeMasterId` is the target slide id (null when not merging); `isMergeMode` is the on flag.
  // `mergeMasterIsFluencyReading` records whether the master is a reading-fluency slide (vs "other") —
  // cards need this to gate reading-fluency sources without every card re-fetching the master slide.
  mergeMasterId: string | null;
  mergeMasterIsFluencyReading: boolean;
  isMergeMode: boolean;
  startMergeMode: (masterId: string, masterIsFluencyReading: boolean) => void;
  exitMergeMode: () => void;

  // Modal actions
  toggleMoveModal: (open: boolean) => void;
  toggleMoveSlideModal: (open: boolean) => void;
  toggleTrashModal: (open: boolean) => void;
  toggleHideModal: (open: "hide" | "show" | false) => void;
  toggleDeleteModal: (open: boolean) => void;
  toggleDuplicateSlideModal: (open: string | false) => void;
  toggleDuplicateLessonModal: (open: string | false) => void;
  toggleDuplicateProjectModal: (open: string | false) => void;
  toggleSetTagsModal: (open: boolean) => void;
  toggleSetDomainModal: (open: boolean) => void;
  toggleRestoreModal: (type: "Slides" | "Projects" | "Lessons" | "Items" | false) => void;
  toggleIsSelectionMode: (open: boolean) => void;
  toggleUnlinkModalOpen: (open: UnlinkModalProps | false) => void;
  toggleWarningModal: (
    message: string | false,
    callbacks?: WarningCallbacks | null,
    error?: unknown
  ) => void;
  toggleLinkedParentWarningModal: (open: boolean) => void;

  // Derived state
  isSelectionMode: boolean;
  selectedItemsCount: number;
}

const SelectionContext = createContext<SelectionContextType | null>(null);

export function SelectionProvider({ children }: { children: ReactNode }) {
  // Selection state
  const [selectionState, setSelectionState] = useState<SelectionState>({
    selectedProjects: [],
    selectedLessons: [],
    selectedSlides: [],
  });

  // Merge mode — the target (master) slide id, or null when not in merge mode.
  const [mergeMasterId, setMergeMasterId] = useState<string | null>(null);
  const [mergeMasterIsFluencyReading, setMergeMasterIsFluencyReading] = useState(false);

  // Modal state
  const [modalState, setModalState] = useState<ModalState>({
    isMoveModalOpen: false,
    isMoveSlideModalOpen: false,
    isTrashModalOpen: false,
    isHideModalOpen: false,
    isDeleteModalOpen: false,
    isRestoreModalOpen: false,
    isSelectionMode: false,
    isDuplicateSlideModalOpen: false as string | false,
    isDuplicateLessonModalOpen: false as string | false,
    isDuplicateProjectModalOpen: false as string | false,
    isSetTagsModalOpen: false,
    isSetDomainModalOpen: false,
    isUnlinkModalOpen: false,
    isWarningModalOpen: false,
    isLinkedParentWarningModalOpen: false,
    warningCallbacks: null,
    warningError: null,
  });

  // Derived state - IDs for backward compatibility
  const selectedProjectIds = useMemo(
    () => selectionState.selectedProjects.map((p) => p.id),
    [selectionState.selectedProjects]
  );

  const selectedLessonIds = useMemo(
    () =>
      [...selectionState.selectedLessons]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((l) => l.id),
    [selectionState.selectedLessons]
  );

  const selectedSlideIds = useMemo(
    () =>
      [...selectionState.selectedSlides]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((s) => s.id),
    [selectionState.selectedSlides]
  );

  // Selection actions
  const toggleProject = useCallback((id: string, isLinkedCopy: boolean) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedProjects: prev.selectedProjects.some((p) => p.id === id)
        ? prev.selectedProjects.filter((p) => p.id !== id)
        : [...prev.selectedProjects, { id, isLinkedCopy }],
    }));
  }, []);

  const toggleLesson = useCallback((id: string, isLinkedCopy: boolean, sortOrder?: number) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedLessons: prev.selectedLessons.some((l) => l.id === id)
        ? prev.selectedLessons.filter((l) => l.id !== id)
        : [...prev.selectedLessons, { id, isLinkedCopy, sortOrder: sortOrder ?? 0 }],
    }));
  }, []);

  const toggleSlide = useCallback((id: string, isLinkedCopy: boolean, sortOrder?: number) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedSlides: prev.selectedSlides.some((s) => s.id === id)
        ? prev.selectedSlides.filter((s) => s.id !== id)
        : [...prev.selectedSlides, { id, isLinkedCopy, sortOrder: sortOrder ?? 0 }],
    }));
  }, []);

  const selectProject = useCallback((id: string, isLinkedCopy: boolean) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedProjects: prev.selectedProjects.some((p) => p.id === id)
        ? prev.selectedProjects
        : [...prev.selectedProjects, { id, isLinkedCopy }],
    }));
  }, []);

  const selectProjects = useCallback((items: SelectedItem[]) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedProjects: [
        ...prev.selectedProjects,
        ...items.filter((item) => !prev.selectedProjects.some((p) => p.id === item.id)),
      ],
    }));
  }, []);

  const selectLesson = useCallback((id: string, isLinkedCopy: boolean, sortOrder?: number) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedLessons: prev.selectedLessons.some((l) => l.id === id)
        ? prev.selectedLessons
        : [...prev.selectedLessons, { id, isLinkedCopy, sortOrder: sortOrder ?? 0 }],
    }));
  }, []);

  const selectLessons = useCallback((items: SelectedItem[]) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedLessons: [
        ...prev.selectedLessons,
        ...items.filter((item) => !prev.selectedLessons.some((l) => l.id === item.id)),
      ],
    }));
  }, []);

  const selectSlide = useCallback((id: string, isLinkedCopy: boolean, sortOrder?: number) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedSlides: prev.selectedSlides.some((s) => s.id === id)
        ? prev.selectedSlides
        : [...prev.selectedSlides, { id, isLinkedCopy, sortOrder: sortOrder ?? 0 }],
    }));
  }, []);

  const selectSlides = useCallback((items: SelectedItem[]) => {
    setSelectionState((prev) => ({
      ...prev,
      selectedSlides: [
        ...prev.selectedSlides,
        ...items.filter((item) => !prev.selectedSlides.some((s) => s.id === item.id)),
      ],
    }));
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelectionState({
      selectedProjects: [],
      selectedLessons: [],
      selectedSlides: [],
    });
  }, []);

  // Modal actions
  const toggleMoveModal = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isMoveModalOpen: open }));
  }, []);

  const toggleMoveSlideModal = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isMoveSlideModalOpen: open }));
  }, []);

  const toggleTrashModal = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isTrashModalOpen: open }));
  }, []);

  const toggleHideModal = useCallback((open: "hide" | "show" | false) => {
    setModalState((prev) => ({ ...prev, isHideModalOpen: open }));
  }, []);

  const toggleDeleteModal = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isDeleteModalOpen: open }));
  }, []);

  const toggleRestoreModal = useCallback(
    (type: "Slides" | "Projects" | "Lessons" | "Items" | false) => {
      setModalState((prev) => ({ ...prev, isRestoreModalOpen: type }));
    },
    []
  );

  const toggleIsSelectionMode = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isSelectionMode: open }));
  }, []);

  const toggleDuplicateSlideModal = useCallback((open: string | false) => {
    setModalState((prev) => ({ ...prev, isDuplicateSlideModalOpen: open }));
  }, []);

  const toggleDuplicateLessonModal = useCallback((open: string | false) => {
    setModalState((prev) => ({ ...prev, isDuplicateLessonModalOpen: open }));
  }, []);

  const toggleDuplicateProjectModal = useCallback((open: string | false) => {
    setModalState((prev) => ({ ...prev, isDuplicateProjectModalOpen: open }));
  }, []);

  const toggleSetTagsModal = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isSetTagsModalOpen: open }));
  }, []);

  const toggleSetDomainModal = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isSetDomainModalOpen: open }));
  }, []);

  // Enter merge mode for a given master slide, starting from an empty source selection.
  const startMergeMode = useCallback((masterId: string, masterIsFluencyReading: boolean) => {
    setSelectionState({ selectedProjects: [], selectedLessons: [], selectedSlides: [] });
    setMergeMasterId(masterId);
    setMergeMasterIsFluencyReading(masterIsFluencyReading);
  }, []);

  const exitMergeMode = useCallback(() => {
    setMergeMasterId(null);
    setMergeMasterIsFluencyReading(false);
    setSelectionState({ selectedProjects: [], selectedLessons: [], selectedSlides: [] });
  }, []);

  const toggleUnlinkModalOpen = useCallback((open: UnlinkModalProps | false) => {
    setModalState((prev) => ({ ...prev, isUnlinkModalOpen: open }));
  }, []);

  const toggleWarningModal = useCallback(
    (message: string | false, callbacks?: WarningCallbacks | null, error?: unknown) => {
      setModalState((prev) => ({
        ...prev,
        isWarningModalOpen: message,
        warningCallbacks: callbacks ?? null,
        warningError: error ?? null,
      }));
    },
    []
  );

  const toggleLinkedParentWarningModal = useCallback((open: boolean) => {
    setModalState((prev) => ({ ...prev, isLinkedParentWarningModalOpen: open }));
  }, []);

  // Derived state
  const selectedItemsCount =
    selectionState.selectedProjects.length +
    selectionState.selectedLessons.length +
    selectionState.selectedSlides.length;

  // Unchecking the last box exits selection mode, so the bulk action bar doesn't linger empty.
  useEffect(() => {
    if (modalState.isSelectionMode && selectedItemsCount === 0) {
      setModalState((prev) => ({ ...prev, isSelectionMode: false }));
    }
  }, [modalState.isSelectionMode, selectedItemsCount]);

  const contextValue = useMemo(
    () => ({
      // Selection state
      selectedProjects: selectionState.selectedProjects,
      selectedLessons: selectionState.selectedLessons,
      selectedSlides: selectionState.selectedSlides,

      // Derived state - IDs for backward compatibility
      selectedProjectIds,
      selectedLessonIds,
      selectedSlideIds,

      // Selection actions
      toggleProject,
      toggleLesson,
      toggleSlide,
      selectProject,
      selectProjects,
      selectLesson,
      selectLessons,
      selectSlide,
      selectSlides,
      clearAllSelections,

      // Modal state
      isMoveModalOpen: modalState.isMoveModalOpen,
      isMoveSlideModalOpen: modalState.isMoveSlideModalOpen,
      isTrashModalOpen: modalState.isTrashModalOpen,
      isHideModalOpen: modalState.isHideModalOpen,
      isDeleteModalOpen: modalState.isDeleteModalOpen,
      isRestoreModalOpen: modalState.isRestoreModalOpen,
      isSelectionMode: modalState.isSelectionMode,
      isDuplicateSlideModalOpen: modalState.isDuplicateSlideModalOpen,
      isDuplicateLessonModalOpen: modalState.isDuplicateLessonModalOpen,
      isDuplicateProjectModalOpen: modalState.isDuplicateProjectModalOpen,
      isSetTagsModalOpen: modalState.isSetTagsModalOpen,
      isSetDomainModalOpen: modalState.isSetDomainModalOpen,
      isUnlinkModalOpen: modalState.isUnlinkModalOpen,
      isWarningModalOpen: modalState.isWarningModalOpen,
      isLinkedParentWarningModalOpen: modalState.isLinkedParentWarningModalOpen,
      warningCallbacks: modalState.warningCallbacks,
      warningError: modalState.warningError,

      // Modal actions
      toggleMoveModal,
      toggleMoveSlideModal,
      toggleTrashModal,
      toggleHideModal,
      toggleDeleteModal,
      toggleRestoreModal,
      toggleIsSelectionMode,
      toggleDuplicateSlideModal,
      toggleDuplicateLessonModal,
      toggleDuplicateProjectModal,
      toggleSetTagsModal,
      toggleSetDomainModal,
      toggleUnlinkModalOpen,
      toggleWarningModal,
      toggleLinkedParentWarningModal,

      // Merge mode
      mergeMasterId,
      mergeMasterIsFluencyReading,
      isMergeMode: mergeMasterId !== null,
      startMergeMode,
      exitMergeMode,

      // Derived state
      selectedItemsCount,
    }),
    [
      selectionState,
      modalState,
      selectedProjectIds,
      selectedLessonIds,
      selectedSlideIds,
      toggleProject,
      toggleLesson,
      toggleSlide,
      selectProject,
      selectProjects,
      selectLesson,
      selectLessons,
      selectSlide,
      selectSlides,
      clearAllSelections,
      toggleMoveModal,
      toggleMoveSlideModal,
      toggleTrashModal,
      toggleHideModal,
      toggleDeleteModal,
      toggleRestoreModal,
      toggleIsSelectionMode,
      toggleDuplicateSlideModal,
      toggleDuplicateLessonModal,
      toggleDuplicateProjectModal,
      toggleSetTagsModal,
      toggleSetDomainModal,
      toggleUnlinkModalOpen,
      toggleWarningModal,
      toggleLinkedParentWarningModal,
      mergeMasterId,
      mergeMasterIsFluencyReading,
      startMergeMode,
      exitMergeMode,
      selectedItemsCount,
    ]
  );

  return <SelectionContext.Provider value={contextValue}>{children}</SelectionContext.Provider>;
}

export function useSelection() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error("useSelection must be used within SelectionProvider");
  }
  return context;
}
